import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Moon, Pencil, Trash2, Coffee } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { fmtEur, fmtHours } from "../lib/api";

const DeleteButton = ({ shift, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid={`shift-delete-${shift.id}`} aria-label="Delete shift"
        className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-400">
        <Trash2 className="h-4 w-4" />
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete this shift?</AlertDialogTitle>
        <AlertDialogDescription>
          {format(parseISO(shift.date), "EEEE d MMMM")} · {shift.start_time}–{shift.end_time}. This can't be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="shift-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="shift-delete-confirm" className="bg-red-500 text-white hover:bg-red-600" onClick={() => onDelete(shift.id)}>
          Delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ShiftRow = ({ shift, onDelete, showDate = true }) => {
  const d = parseISO(shift.date);
  return (
    <div data-testid={`shift-row-${shift.id}`}
      className="group flex items-center gap-4 rounded-2xl border border-border/70 bg-card p-4 transition-colors duration-200 hover:border-amber-500/40">
      {showDate && (
        <div className="w-12 shrink-0 text-center">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{format(d, "EEE")}</div>
          <div className="font-heading text-3xl font-extrabold leading-none">{format(d, "d")}</div>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-num text-sm font-bold">{shift.start_time} – {shift.end_time}</span>
          {shift.overnight && (
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-300">
              <Moon className="h-3 w-3" /> Night
            </span>
          )}
          {shift.overtime_minutes > 0 && (
            <span data-testid={`shift-ot-${shift.id}`} className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-num text-[10px] font-bold text-emerald-300">
              +{fmtHours(shift.overtime_minutes)} OT
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
          <span data-testid={`shift-paid-${shift.id}`}>{fmtHours(shift.paid_minutes)} paid</span>
          <span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3" />{shift.effective_break}m{shift.auto_break ? " auto" : ""}</span>
          {shift.note && <span className="truncate">{shift.note}</span>}
        </div>
      </div>
      <div className="text-right">
        <div data-testid={`shift-earnings-${shift.id}`} className="font-num text-sm font-bold text-amber-400">{fmtEur(shift.earnings)}</div>
      </div>
      <div className="flex items-center">
        <Link to={`/shifts/${shift.id}/edit`} data-testid={`shift-edit-${shift.id}`} aria-label="Edit shift"
          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          <Pencil className="h-4 w-4" />
        </Link>
        {onDelete && <DeleteButton shift={shift} onDelete={onDelete} />}
      </div>
    </div>
  );
};
